import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import i18n from '@/i18n';
import {
  courseAppearanceService,
  type UpdateCourseMenuItemInput,
  type CourseMenuItem,
} from '@/services/course-appearance.service';
import { appearanceKeys } from './queryKeys';

interface Vars {
  itemId: string;
  input: UpdateCourseMenuItemInput;
}

/** Update a per-course menu item optimistically. */
export function useUpdateCourseMenuItem(productId: string) {
  const qc = useQueryClient();
  const key = appearanceKeys.menuItems(productId);

  return useMutation({
    mutationFn: ({ itemId, input }: Vars) =>
      courseAppearanceService.updateMenuItem(productId, itemId, input),
    onMutate: async ({ itemId, input }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<CourseMenuItem[]>(key);
      if (prev) {
        qc.setQueryData<CourseMenuItem[]>(
          key,
          prev.map((i) =>
            i.id === itemId ? ({ ...i, ...input } as CourseMenuItem) : i,
          ),
        );
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      toast.error(i18n.t('adminAppearance:toasts.updateMenuItemError'));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}
